import { useCallback, useRef } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseFormReturn } from 'react-hook-form';
import toast from 'react-hot-toast';

import type { AccountCreateSchema, AccountIndexSchema } from '@liscio/api';
import { useApiClient, useTrackEvent } from '@liscio/common';

import { applyCreateAccountApiErrors } from './applyCreateAccountApiErrors';
import type { CreateAccountFormValues } from './createAccountValidationSchema';
import { mapCreateAccountFormValuesToApiPayload } from './mapCreateAccountFormValuesToApiPayload';
import { toErrorObjectArray } from 'fetch-utils/api-errors';
import { useHomeData } from 'fetch-utils/users/user-hooks';

const CREATE_FIRM_ACCOUNT_URL = '/api/v5/firms/accounts';
// Prefixes of the Orval keys the Files page caches its accounts list and persisted folder
// hierarchy under (every parameter variant), so a new Account appears there at once (FVA-008).
const FILES_ACCOUNTS_QUERY_KEY = ['/api/v5/firms/files/accounts'] as const;
const FILES_FOLDERS_QUERY_KEY = ['/api/v5/firms/folders'] as const;

const jsonRequestHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
} as const;

export type UseCreateAccountSubmitOptions = {
  source?: string;
  onSuccess?: (account: AccountIndexSchema) => void;
};

export function useCreateAccountSubmit(
  formMethods: UseFormReturn<CreateAccountFormValues>,
  options?: UseCreateAccountSubmitOptions
) {
  const { client } = useApiClient();
  const queryClient = useQueryClient();
  const { handleSubmit, setError } = formMethods;
  const { trackEvent } = useTrackEvent();
  const { data: homeData } = useHomeData();

  // Refs keep the mutation closure up-to-date without recreating the mutation.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  const homeDataRef = useRef(homeData);
  homeDataRef.current = homeData;

  const { mutate, isLoading } = useMutation({
    mutationFn: async (payload: AccountCreateSchema) => {
      const { data } = await client.post<AccountIndexSchema>(
        CREATE_FIRM_ACCOUNT_URL,
        payload,
        { headers: jsonRequestHeaders }
      );
      return data;
    },
    onSuccess: (account) => {
      if (!account.uuid) {
        toast.error('Account was created but no id was returned.');
        return;
      }
      queryClient.invalidateQueries({ queryKey: FILES_ACCOUNTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: FILES_FOLDERS_QUERY_KEY });
      const { source } = optionsRef.current ?? {};
      if (source) {
        trackEventRef.current('account_created', {
          source,
          ...(source === 'onboarding' ? { onboarding_step: 'account_created' } : {}),
          user_role: 'firm_employee',
          company_name: homeDataRef.current?.data?.userpilot_data?.firm_name,
          account_id: account.id,
        });
      }
      optionsRef.current?.onSuccess?.(account);
    },
    onError: (error) => {
      const apiError = toErrorObjectArray(error);
      const message = applyCreateAccountApiErrors(apiError, setError);
      toast.error(message);
    },
  });

  const submitCreateAccount = useCallback(
    () =>
      handleSubmit((values) => {
        mutate(mapCreateAccountFormValuesToApiPayload(values));
      })(),
    [handleSubmit, mutate]
  );

  return {
    submitCreateAccount,
    isSubmitting: isLoading,
  };
}
